import React from 'react';
import ReactDOM from 'react-dom/client';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LangProvider } from './i18n';
import { isDragonTiger, isGerman, isBrazil, BRAND_NAME } from './lib/app-variant';
import './index.css';

// TON Connect manifest URL — canonical cricketflip.com (Cloudflare + SSL) for the
// default build. CF-MAINNET-72: was stale pointing at vercel.app; canonical domain
// is live + preferred + matches the manifest's self-declared `url` field.
// CF-JINLONGHU-MANIFEST-V1 (sweep B-4): the wallet-connect (Tonkeeper) approval sheet
// renders the manifest's `name` + `url` — for 金龙虎 it must NOT show "CricketFlip" /
// cricketflip.com right before a deposit. The dragontiger build writes its own 金龙虎
// manifest into dist (vite.config.ts writeBundle), served from the jinlonghu host;
// the default cricketflip build keeps its manifest byte-unchanged.
// CF-GERMAN-VARIANT-V1: the german build serves its own „Kopf oder Zahl" manifest
// (written into dist by vite.config.ts germanMetaPlugin) from the german host, so
// the Tonkeeper approval sheet never shows "CricketFlip" / cricketflip.com.
const MANIFEST_URL = isDragonTiger
    ? 'https://jinlonghu.vercel.app/tonconnect-manifest.json'
    : isGerman
        ? 'https://muenzo.xyz/tonconnect-manifest.json'
        : isBrazil
            ? 'https://moedaflip.xyz/tonconnect-manifest.json'
            : 'https://cricketflip.com/tonconnect-manifest.json';

// CF-MAINNET-41: wrap the whole app tree in ErrorBoundary. A rendering error in
// any component now shows a truthful fallback UI + reload action instead of
// white-screen-of-death. Placed OUTSIDE TonConnectUIProvider so provider-init
// errors are also caught (rare but possible during wallet ID loading).
// CF-DEMO-TIERS-LAYOUT-RESPONSIVE-V1: set the variant theme attribute
// SYNCHRONOUSLY here — before createRoot().render() and the first paint — so the
// variant-scoped [data-theme="dragon-tiger"] responsive rules (root-font clamp +
// collapsed choice-card/disc sizing) are active from frame 1. Previously the
// attribute was set only in LangProvider's mount effect (post first paint), so the
// first frame rendered the full-size (16px-root) layout whose taller discs/cards
// pushed the FLIP CTA under the fixed bottom nav until the effect ran and the
// layout collapsed — a transient phone-fit failure on short screens. The clamp's
// `100vh` fallback yields the correct root font immediately, so frame 1 fits.
// Default build leaves the attribute unset → live cricketflip.com untouched.
// (LangProvider still re-asserts it idempotently for safety.)
if (isDragonTiger) {
    document.documentElement.dataset.theme = 'dragon-tiger';
    // CF-JINLONGHU-BRAND-LEAK-V1 (PL #8640): the static index.html <title> is the
    // English "CricketFlip — Coin toss on TON" (shared HTML). Override it to the
    // 金龙虎 brand for the variant so the Telegram webview title never shows the
    // English name. Default build leaves the HTML <title> untouched.
    document.title = '金龙虎';
} else if (isGerman) {
    // CF-GERMAN-VARIANT-V1: set the black/gold german theme SYNCHRONOUSLY before
    // first paint (same pre-paint rationale as dragontiger) so the
    // [data-theme="german"] tokens are active from frame 1, and override the
    // shared English <title> to the german brand. Default + dragontiger untouched.
    document.documentElement.dataset.theme = 'german';
    document.title = BRAND_NAME;
} else if (isBrazil) {
    // CF-BRAZIL-VARIANT-V1: set the emerald/gold „MoedaFlip" (Cara ou Coroa) theme
    // SYNCHRONOUSLY before first paint (same pre-paint rationale as dragontiger /
    // german) so the [data-theme="brazil"] tokens are active from frame 1, and
    // override the shared English <title> to the brazil brand. Other builds untouched.
    document.documentElement.dataset.theme = 'brazil';
    document.title = BRAND_NAME;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <ErrorBoundary>
            <TonConnectUIProvider manifestUrl={MANIFEST_URL}>
                <LangProvider>
                    <App />
                </LangProvider>
            </TonConnectUIProvider>
        </ErrorBoundary>
    </React.StrictMode>
);
